import { useEffect, useMemo, useRef, useState } from 'react';
import {
  cueHighlights,
  cueIsActive,
  NowYouTry,
  usePrefersReducedMotion,
  useMountSequence,
  VizCard,
  type VizCue,
} from '../vizShared';

// Dilution — interactive M₁V₁ = M₂V₂. Pick which variable to solve for, drag the
// other three, and watch a stock beaker + water + diluted beaker reflect the
// concentration change (color intensity ∝ molarity, fill height ∝ volume).
//
// config: { m1?, v1?, m2?, v2?, solveFor?, title? }
//   M in mol/L, V in mL. Resilient: defaults to 1 M / 10 mL → ? M / 100 mL.

export interface DilutionConfig {
  title?: string;
  m1?: number;
  v1?: number;
  m2?: number;
  v2?: number;
  solveFor?: 'M1' | 'V1' | 'M2' | 'V2';
}

interface Props {
  config?: DilutionConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

type Var = 'M1' | 'V1' | 'M2' | 'V2';

const RANGES: Record<Var, { min: number; max: number; step: number; unit: string; label: string }> = {
  M1: { min: 0.1, max: 12, step: 0.1, unit: 'M', label: 'Stock conc.' },
  V1: { min: 1, max: 200, step: 1, unit: 'mL', label: 'Stock vol.' },
  M2: { min: 0.01, max: 12, step: 0.01, unit: 'M', label: 'Final conc.' },
  V2: { min: 1, max: 1000, step: 1, unit: 'mL', label: 'Final vol.' },
};

function clampRange(v: Var, x: number) {
  const r = RANGES[v];
  if (!Number.isFinite(x)) return r.min;
  return Math.min(r.max, Math.max(r.min, x));
}

const sub = (s: string) => s.replace('1', '₁').replace('2', '₂');

export default function Dilution({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  const [solveFor, setSolveFor] = useState<Var>(config.solveFor ?? 'M2');
  const [m1, setM1] = useState(clampRange('M1', config.m1 ?? 1));
  const [v1, setV1] = useState(clampRange('V1', config.v1 ?? 10));
  const [m2, setM2] = useState(clampRange('M2', config.m2 ?? 0.1));
  const [v2, setV2] = useState(clampRange('V2', config.v2 ?? 100));
  const [revealStep, setRevealStep] = useState(0); // worked-steps reveal (cue.step)
  const [spotlight, setSpotlight] = useState<string | null>(null);
  const interactedRef = useRef(false);

  const mark = () => {
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'dilution' });
    }
  };

  // ---- Self-demo: add solvent — final volume rises while the diluted color fades.
  const demoStartV2 = clampRange('V2', config.v2 ?? 100);
  const demo = useMountSequence(
    [
      { at: 200, run: () => { setSolveFor('M2'); setV2(clampRange('V2', Math.max(RANGES.V2.min, v1 + 5))); } },
      { at: 650, run: () => setV2(clampRange('V2', 40)) },
      { at: 1050, run: () => setV2(clampRange('V2', 90)) },
      { at: 1450, run: () => setV2(clampRange('V2', 160)) },
      { at: 1900, run: () => { setSolveFor(config.solveFor ?? 'M2'); setV2(demoStartV2); } },
    ],
    { skip: reduced || hasCue, onSettle: () => { setSolveFor(config.solveFor ?? 'M2'); setV2(demoStartV2); } },
  );
  const cancelDemo = () => { if (demo.running) demo.cancel(); };

  // ---- Cue: progress → added solvent (final volume); step → reveal worked steps;
  // highlight → spotlight a term (M1/V1/M2/V2).
  useEffect(() => {
    if (cue?.progress == null) return;
    setSolveFor('M2');
    // progress drives water added: V2 from V1 up to the V2 max.
    const lo = Math.max(RANGES.V2.min, v1);
    const val = lo + Math.max(0, Math.min(1, cue.progress)) * (RANGES.V2.max - lo);
    setV2(clampRange('V2', val));
  }, [cue?.progress, v1]);

  useEffect(() => {
    if (cue?.step == null) return;
    setRevealStep(Math.max(0, Math.min(3, Math.round(cue.step))));
  }, [cue?.step]);

  useEffect(() => {
    const hi = cueHighlights(cue);
    if (hi.length === 0) { setSpotlight(null); return; }
    const term = (['m1', 'v1', 'm2', 'v2'] as const).find((k) => hi.includes(k));
    setSpotlight(term ? term.toUpperCase() : null);
  }, [cue]);

  // Solve so M₁V₁ = M₂V₂ holds for the chosen unknown.
  const solved = useMemo(() => {
    switch (solveFor) {
      case 'M1': return (m2 * v2) / v1;
      case 'V1': return (m2 * v2) / m1;
      case 'M2': return (m1 * v1) / v2;
      case 'V2': return (m1 * v1) / m2;
    }
  }, [solveFor, m1, v1, m2, v2]);

  const M1 = solveFor === 'M1' ? solved : m1;
  const V1 = solveFor === 'V1' ? solved : v1;
  const M2 = solveFor === 'M2' ? solved : m2;
  const V2 = solveFor === 'V2' ? solved : v2;
  const waterAdded = Math.max(0, V2 - V1);

  const sliderVars: Var[] = (['M1', 'V1', 'M2', 'V2'] as Var[]).filter((x) => x !== solveFor);
  const setter: Record<Var, (x: number) => void> = { M1: setM1, V1: setV1, M2: setM2, V2: setV2 };
  const stateVal: Record<Var, number> = { M1, V1, M2, V2 };

  const beakerTransition = reduced ? undefined : 'all 320ms ease-out';

  // Beaker render helper: fill height ∝ volume, blue alpha ∝ concentration.
  const beaker = (vol: number, conc: number, maxVol: number, maxConc: number, label: string) => {
    const hh = 90;
    const fill = Math.min(1, Math.max(0.04, vol / maxVol)) * (hh - 8);
    const alpha = Math.min(0.9, Math.max(0.06, conc / maxConc));
    return (
      <div className="flex flex-col items-center">
        <svg viewBox="0 0 70 100" className="w-20" role="img" aria-label={`${label}: ${fmt(conc)} molar, ${fmt(vol)} milliliters`}>
          <path d="M12 6 L12 88 Q12 94 18 94 L52 94 Q58 94 58 88 L58 6" fill="var(--ws-surface-2)" stroke="var(--ws-border-strong)" strokeWidth="1.5" />
          <rect x="13" y={92 - fill} width="44" height={fill} fill={`rgba(59,130,196,${alpha})`} style={{ transition: beakerTransition }} />
          <rect x="13" y={92 - fill} width="44" height="3" fill={`rgba(59,130,196,${Math.min(1, alpha + 0.2)})`} style={{ transition: beakerTransition }} />
        </svg>
        <span className="mt-0.5 text-[10px] text-ws-ink-3">{label}</span>
        <span className="font-mono text-[11px] font-semibold text-ws-ink">{fmt(conc)} M</span>
        <span className="font-mono text-[10px] text-ws-ink-3">{fmt(vol)} mL</span>
      </div>
    );
  };

  const term = (key: string, text: string) => (
    <span
      className={`rounded px-1 transition-colors ${spotlight === key ? 'bg-ws-accent-quiet font-semibold text-ws-accent-ink' : ''}`}
    >
      {text}
    </span>
  );

  // Worked steps for cue.step reveal.
  const steps = [
    `Conserve moles: M₁V₁ = M₂V₂`,
    `Plug in: ${fmt(M1)} × ${fmt(V1)} = ${fmt(M2)} × ${fmt(V2)}`,
    `Solve ${sub(solveFor)}: ${sub(solveFor)} = ${fmt(solved)} ${RANGES[solveFor].unit}`,
    `Water added = V₂ − V₁ = ${fmt(waterAdded)} mL`,
  ];

  return (
    <VizCard
      reducedMotion={reduced}
      eyebrow="Dilution"
      title={config.title ?? 'Dilution'}
      subtitle="Adding solvent lowers concentration but conserves moles of solute"
      hint="Pick a variable to solve, drag sliders · ←/→ to adjust · auto-dilutes a demo on load"
      actions={<span className="font-mono text-sm font-semibold text-ws-accent">M₁V₁ = M₂V₂</span>}
    >
      <div className="relative w-full select-none">
        <NowYouTry show={demo.done && !reduced && !hasCue} />

        <div className="mb-3 flex items-center gap-1 text-xs">
          <span className="text-ws-ink-3">Solve for:</span>
          {(['M1', 'V1', 'M2', 'V2'] as Var[]).map((x) => (
            <button
              key={x}
              type="button"
              onClick={() => { cancelDemo(); setSolveFor(x); mark(); }}
              className={`rounded-sm px-2 py-1 font-mono font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent focus-visible:ring-offset-1 ${
                solveFor === x ? 'bg-ws-accent text-ws-on-accent' : 'bg-ws-tile text-ws-ink-2 hover:bg-ws-surface-2'
              }`}
              aria-pressed={solveFor === x}
              aria-label={`Solve for ${RANGES[x].label}`}
            >
              {sub(x)}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          {/* Beaker triptych */}
          <div className="flex items-center gap-1">
            {beaker(V1, M1, RANGES.V2.max, RANGES.M1.max, 'Stock')}
            <div className="flex flex-col items-center px-1 text-ws-ink-3">
              <span className="text-lg">+</span>
              <span className="text-[9px] leading-tight">{fmt(waterAdded)} mL<br />water</span>
            </div>
            <span className="text-lg text-ws-ink-3">→</span>
            {beaker(V2, M2, RANGES.V2.max, RANGES.M1.max, 'Diluted')}
          </div>

          {/* Sliders */}
          <div className="min-w-0 flex-1">
            {sliderVars.map((x) => {
              const r = RANGES[x];
              return (
                <div key={x} className="mb-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-ws-ink-2">{r.label} ({sub(x)})</span>
                    <span className="font-mono font-semibold tabular-nums text-ws-ink">{fmt(stateVal[x])} {r.unit}</span>
                  </div>
                  <input
                    type="range"
                    min={r.min}
                    max={r.max}
                    step={r.step}
                    value={stateVal[x]}
                    onPointerDown={cancelDemo}
                    onKeyDown={cancelDemo}
                    onChange={(e) => { setter[x](clampRange(x, parseFloat(e.target.value))); mark(); }}
                    className="w-full accent-ws-accent"
                    aria-label={`${r.label} in ${r.unit}`}
                    aria-valuetext={`${fmt(stateVal[x])} ${r.unit}`}
                  />
                </div>
              );
            })}
            <div className="mt-1 rounded-md border border-ws-accent bg-ws-accent-quiet px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-ws-accent-ink">Solved · {RANGES[solveFor].label}</div>
              <div className="font-mono text-lg font-bold text-ws-accent-ink">
                {sub(solveFor)} = {fmt(solved)} {RANGES[solveFor].unit}
              </div>
            </div>
          </div>
        </div>

        {/* Worked steps (revealed progressively via cue.step) */}
        {cue?.step != null && (
          <ol className="mt-3 space-y-1 rounded-md border border-ws-border bg-ws-surface-2 px-3 py-2 text-xs text-ws-ink-2">
            {steps.slice(0, revealStep + 1).map((s, i) => (
              <li key={i} className="font-mono">{i + 1}. {s}</li>
            ))}
          </ol>
        )}

        <p className="mt-2 text-xs text-ws-ink-3">
          Moles of solute stay constant: {term('M1', 'M₁')}{term('V1', 'V₁')} = {fmt(M1 * V1)} = {term('M2', 'M₂')}{term('V2', 'V₂')} = {fmt(M2 * V2)} (mmol). Diluting drops concentration but not the amount of solute.
        </p>
      </div>
    </VizCard>
  );
}

function fmt(x: number) {
  if (!Number.isFinite(x)) return '—';
  if (Math.abs(x) >= 100) return x.toFixed(0);
  if (Math.abs(x) >= 1) return x.toFixed(2);
  return x.toFixed(3);
}
